import axios from 'axios';

export interface SchoolPortalOrgResult {
  admin: {
    id: string;
    name: string;
    email: string | null;
    mobile: string | null;
    role: string;
    organizationId: string;
    mustChangePassword: boolean;
    scope: 'organization';
  };
  organization: {
    id: string;
    name: string;
    maxSchoolsAllowed: number;
    status: string;
  };
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
}

export interface SchoolPortalTenantResult {
  user: {
    id: string;
    name: string;
    email: string | null;
    role: string;
    roles: string[];
    schoolId: string;
    schoolSlug: string;
    scope: 'tenant';
  };
  school: { id: string; slug: string; name: string; status: string };
  tokens: { accessToken: string; refreshToken: string; expiresIn: number };
}

export type SchoolPortalLoginResult =
  | SchoolPortalOrgResult
  | SchoolPortalTenantResult;

export interface SchoolPortalMe {
  id: string;
  name: string;
  email: string | null;
  role: string;
  roles: string[];
  schoolId: string;
  schoolName: string | null;
  schoolSlug: string | null;
}

// Raw axios (no tenant header, no stale-token refresh interceptor) for login
// — same reasoning as OrgAuthApi.login.
export const AuthApi = {
  /** Backend decides org-admin vs direct school-user — see docs/DEVIATIONS.md. */
  login: async (
    login: string,
    password: string,
  ): Promise<SchoolPortalLoginResult> => {
    const r = await axios.post('/api/v1/auth/school/login', { login, password });
    return (r.data?.data ?? r.data) as SchoolPortalLoginResult;
  },

  refresh: async (
    refreshToken: string,
  ): Promise<{ tokens: SchoolPortalTenantResult['tokens'] }> => {
    const r = await axios.post('/api/v1/auth/refresh', { refreshToken });
    return (r.data?.data ?? r.data) as { tokens: SchoolPortalTenantResult['tokens'] };
  },

  logout: async (refreshToken: string): Promise<void> => {
    await axios.post('/api/v1/auth/logout', { refreshToken });
  },

  me: async (accessToken: string): Promise<SchoolPortalMe> => {
    const r = await axios.get('/api/v1/auth/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return (r.data?.data ?? r.data) as SchoolPortalMe;
  },
};

export function isOrgResult(
  result: SchoolPortalLoginResult,
): result is SchoolPortalOrgResult {
  return 'admin' in result;
}
